<template>
  <div id="app" :dir="currentDirection">
    <div class="menu-overlay" :class="{ active: mobileMenuOpen }" @click="closeMobileMenu"></div>
    <!-- Header -->
    <header :class="{ 'scrolled': isScrolled }">
      <div class="container">
        <router-link to="/" class="logo">
          <img src="/img/finio-logo.webp" alt="Finio Logo" class="logo-img" />
          <h1 style="margin-left: -13px;margin-top: 13px;">inio</h1>
        </router-link>
        <nav :class="{ active: mobileMenuOpen }">
          <ul>
            <li v-for="link in headerLinks" :key="link.path">
              <router-link :to="link.path" @click="closeMobileMenu">{{ $t(link.label) }}</router-link>
            </li>
          </ul>
        </nav>
        <div class="language-switcher">
          <label for="language-select" class="sr-only">Select Language</label>
          <select id="language-select" v-model="selectedLanguage" @change="changeLanguage" aria-label="Select language">
            <option value="en">English</option>
            <option value="fr">Français</option>
            <option value="es">Español</option>
            <option value="ar">العربية</option>
            <option value="el">Ελληνικά</option>
          </select>
        </div>
        <div class="mobile-menu" @click="toggleMobileMenu">
          <div ref="lottieContainer" style="width: 50px; height: 50px;"></div>
        </div>
      </div>
    </header>

    <!-- Main Content (Vue Router View) -->
    <router-view></router-view>

    <!-- Footer -->
    <footer class="footer-card">
      <div class="container">
        
        <div class="footer-content">
          <!-- Brand Column -->
          <div class="footer-brand-col">
            <div class="footer-logo">Finio <span>PRO</span></div>
            <div class="footer-socials">
              <a href="https://www.facebook.com/share/19xsFHsXC8/?mibextid=wwXIfr" class="social-icon" target="_blank" rel="noopener noreferrer" aria-label="Visit our Facebook page"><i class="fab fa-facebook-f" aria-hidden="true"></i></a>
              <a href="https://www.linkedin.com/company/109772049" class="social-icon" target="_blank" rel="noopener noreferrer" aria-label="Visit our LinkedIn page"><i class="fab fa-linkedin-in" aria-hidden="true"></i></a>
              <a href="https://www.instagram.com/finiosolutions" class="social-icon" target="_blank" rel="noopener noreferrer" aria-label="Visit our Instagram page"><i class="fab fa-instagram" aria-hidden="true"></i></a>
            </div>
          </div>

          <!-- Links Columns -->
          <div class="footer-links-grid">
            
            <!-- Product -->
            <div class="footer-col">
              <h4>{{ $t('footer_col_product') }}</h4>
              <ul>
                <li v-for="link in footerProductLinks" :key="link.path">
                  <router-link :to="link.path">{{ $t(link.label) }}</router-link>
                </li>
              </ul>
            </div>

            <!-- Resources -->
            <div class="footer-col">
              <h4>{{ $t('footer_col_resources') }}</h4>
              <ul>
                <li v-for="link in footerResourcesLinks" :key="link.path">
                  <router-link :to="link.path">{{ link.isRaw ? link.label : $t(link.label) }}</router-link>
                </li>
              </ul>
            </div>

            <!-- Terms -->
            <div class="footer-col">
              <h4>{{ $t('footer_col_terms') }}</h4>
              <ul>
                <li v-for="link in footerTermsLinks" :key="link.path">
                  <router-link :to="link.path">{{ $t(link.label) }}</router-link>
                </li>
              </ul>
            </div>

            <!-- Connect -->
            <div class="footer-col">
              <h4>{{ $t('footer_col_connect') }}</h4>
              <ul>
                <li><router-link to="/contact">{{ $t('contact_us') }}</router-link></li>
                <li><router-link to="/about">{{ $t('nav_about_us') }}</router-link></li>
                <li><a href="mailto:contact@finio.pro">contact@finio.pro</a></li>
              </ul>
            </div>

          </div>
        </div>

        <div class="footer-bottom-bar">
          <div class="copyright">{{ $t('copyright') }}</div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { ref, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router'; // Import useRoute
import { headerLinks, footerProductLinks, footerResourcesLinks, footerTermsLinks } from '@/constants/navigation';

export default {
  setup() {
    const { locale, t } = useI18n();
    const route = useRoute(); // Use route
    const mobileMenuOpen = ref(false);

    const selectedLanguage = ref(localStorage.getItem('selectedLanguage') || 'en');
    const currentDirection = computed(() => (locale.value === 'ar' ? 'rtl' : 'ltr'));

    const isScrolled = ref(false);
    const lottieContainer = ref(null);
    let anim = null;
    let observer = null; // Store observer instance

    const handleScroll = () => {
      isScrolled.value = window.scrollY > 50;
    };

    const touchStartX = ref(0);
    const touchEndX = ref(0);
    const touchStartY = ref(0);
    const touchEndY = ref(0);

    const handleTouchStart = (e) => {
      touchStartX.value = e.changedTouches[0].screenX;
      touchStartY.value = e.changedTouches[0].screenY;
    };

    const handleTouchEnd = (e) => {
      touchEndX.value = e.changedTouches[0].screenX;
      touchEndY.value = e.changedTouches[0].screenY;
      handleSwipe();
    };

    const handleSwipe = () => {
      const SWIPE_THRESHOLD = 50; // Minimum distance for a swipe
      const EDGE_THRESHOLD = 30; // Only allow swipe from edge (px)
      const diffX = touchEndX.value - touchStartX.value;
      const diffY = touchEndY.value - touchStartY.value;
      const isRTL = currentDirection.value === 'rtl';

      // Ignore if vertical swipe (scrolling) is dominant
      if (Math.abs(diffY) > Math.abs(diffX)) return;

      if (Math.abs(diffX) < SWIPE_THRESHOLD) return;

      if (mobileMenuOpen.value) {
        // Closing Logic
        // LTR: Swipe Right (positive distance) -> Close
        // RTL: Swipe Left (negative distance) -> Close
        if ((!isRTL && diffX > 0) || (isRTL && diffX < 0)) {
          closeMobileMenu();
        }
      } else {
        // Opening Logic - Only from edge
        // LTR: Swipe Left (negative distance) -> Start must be > screenWidth - EDGE
        // RTL: Swipe Right (positive distance) -> Start must be < EDGE
        
        const screenWidth = window.innerWidth;
        const isFromRightEdge = touchStartX.value > (screenWidth - EDGE_THRESHOLD);
        const isFromLeftEdge = touchStartX.value < EDGE_THRESHOLD;

        // LTR: Swipe Left (open from right)
        if (!isRTL && diffX < 0 && isFromRightEdge) {
           openMobileMenu();
        }
        // RTL: Swipe Right (open from left)
        else if (isRTL && diffX > 0 && isFromLeftEdge) {
           openMobileMenu();
        }
      }
    };

    const openMobileMenu = () => {
        if (!mobileMenuOpen.value) {
            mobileMenuOpen.value = true;
            if (anim) {
                anim.setDirection(1);
                anim.play();
            }
        }
    };

    const toggleMobileMenu = () => {
      if (mobileMenuOpen.value) {
          closeMobileMenu();
      } else {
          openMobileMenu();
      }
    };

    const closeMobileMenu = () => {
      mobileMenuOpen.value = false;
      if (anim) {
        anim.setDirection(-1);
        anim.play();
      }
    };
    
    // Scroll Lock Watcher
    watch(mobileMenuOpen, (isOpen) => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    });

    onMounted(() => {
       // Add global touch listeners
       window.addEventListener('touchstart', handleTouchStart);
       window.addEventListener('touchend', handleTouchEnd);
    });

    const changeLanguage = () => {
      locale.value = selectedLanguage.value;
      localStorage.setItem('selectedLanguage', selectedLanguage.value);
      document.title = t('document_title'); 
    };

    // Reusable function to observe elements
    const observeElements = () => {
      if (!observer) return;
      
      // Small delay to ensure DOM is ready after navigation
      setTimeout(() => {
        const animatedElements = document.querySelectorAll('.animate-fade-up');
        animatedElements.forEach(el => observer.observe(el));
      }, 100);
    };

    onMounted(() => {
      import('lottie-web').then((lottie) => {
        anim = lottie.default.loadAnimation({
          container: lottieContainer.value,
          renderer: 'svg',
          loop: false,
          autoplay: false,
          path: '/assets/finio_animation.json'
        });
      });

      window.addEventListener('scroll', handleScroll);
      
      locale.value = selectedLanguage.value;
      if (t('document_title')) document.title = t('document_title'); 

      // Initialize Observer
      const observerOptions = {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px"
      };

      observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      // Initial observation
      observeElements();

      // Ripple effect initialization
      setTimeout(() => {
        document.querySelectorAll("nav a").forEach(link => {
          link.addEventListener("mousemove", function(e) {
            const rect = this.getBoundingClientRect();
            this.style.setProperty("--x", (e.clientX - rect.left) + "px");
            this.style.setProperty("--y", (e.clientY - rect.top) + "px");
          });
        });
      }, 500);
    });
    
    // Watch for route changes to re-trigger animation observation
    watch(
      () => route.path,
      () => {
        observeElements();
        window.scrollTo(0, 0); // Ensure scroll to top on nav
      }
    );

    watch(locale, (newLocale) => {
      document.documentElement.setAttribute('lang', newLocale);
      document.documentElement.setAttribute('dir', newLocale === 'ar' ? 'rtl' : 'ltr');
    }, { immediate: true });

    return {
      mobileMenuOpen,
      toggleMobileMenu,
      closeMobileMenu,
      selectedLanguage,
      changeLanguage,
      currentDirection,
      isScrolled,
      lottieContainer,

      headerLinks,
      footerProductLinks,
      footerResourcesLinks,
      footerTermsLinks
    };
  },
};
</script>

/* Global styles imported in main.js */

